import { Header, StatsCard } from "@general-backoffice/core";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Col, Container, Row } from "reactstrap";

import { adminStatsEndpoints, type TotalChargesData } from "../../../../../api";
import MonthlyEarningsGraphCard from "../../../../../components/adminStats/MonthlyEarningsGraphCard";
// Components
import YearChargesListCard from "../../../../../components/adminStats/YearChargesListCard";
// Hooks
import usePagination from "../../../../../components/hooks/usePagination";
import useFetch from "../../../../../hooks/useFetch";

const MRR = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.adminStatsPages.mrr" });
  const now = moment().locale("en");
  const navigate = useNavigate();
  const [currYearStats, setCurrYearStats] = useState<TotalChargesData | undefined>();

  // Year stats + pagination
  const [year, setYear] = useState(moment().format("YYYY"));
  const selectedYear = Number(year);

  const { data: statsY1, isLoading: isLoadingY1 } = useFetch(
    () => adminStatsEndpoints.getYearMonthlyCharges(selectedYear).then(({ data }) => data),
    { deps: [selectedYear] }
  );
  const { data: statsY2, isLoading: isLoadingY2 } = useFetch(
    () => adminStatsEndpoints.getYearMonthlyCharges(selectedYear - 1).then(({ data }) => data),
    { deps: [selectedYear] }
  );
  const { data: statsY3, isLoading: isLoadingY3 } = useFetch(
    () => adminStatsEndpoints.getYearMonthlyCharges(selectedYear - 2).then(({ data }) => data),
    { deps: [selectedYear] }
  );

  const isLoadingStats = isLoadingY1 || isLoadingY2 || isLoadingY3;

  const { perUser: unsortedPerUser, total } = statsY1 || {};

  // Build yearDataMap for the graph card
  const yearDataMap: Record<number, TotalChargesData> = useMemo(() => {
    const map: Record<number, TotalChargesData> = {};
    if (statsY3?.total) map[selectedYear - 2] = statsY3.total;
    if (statsY2?.total) map[selectedYear - 1] = statsY2.total;
    if (statsY1?.total) map[selectedYear] = statsY1.total;
    return map;
  }, [statsY1, statsY2, statsY3, selectedYear]);

  // Sort by total amount descending
  const perUser = useMemo(
    () => [...(unsortedPerUser || [])].sort((a, b) => (b.totalCharged ?? 0) - (a.totalCharged ?? 0)),
    [unsortedPerUser]
  );

  // Store curr year total stats

  useEffect(() => {
    if (!currYearStats) setCurrYearStats(statsY1?.total);
  }, [currYearStats, statsY1]);
  const [currMonthCharged, totalMonthPending, totalMonthPaidOut] = useMemo((): [number, number, number] => {
    if (!currYearStats) return [0, 0, 0];
    const monthData = currYearStats?.perMonth?.[now.format("M")];
    if (!monthData) return [0, 0, 0];
    const values = Object.values(monthData);
    return [values[0] ?? 0, values[1] ?? 0, values[2] ?? 0];
  }, [currYearStats, now]);

  const { pageItems: pageUsers, ...usersPaginationProps } = usePagination(perUser, { pageSize: 10 });

  return (
    <>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12} md={4}>
          <StatsCard.Number
            color="default"
            number={currMonthCharged}
            suffix="€"
            title={t("chargedThisMonth")}
            icon="fas fa-file-invoice"
            isDark
          />
        </Col>
        <Col xs={12} md={4}>
          <div
            onClick={() => navigate("/billing/missing-payments")}
            style={{ cursor: "pointer" }}
            title={t("seeMissingPayments")}
          >
            <StatsCard.Number
              color="danger"
              number={totalMonthPending}
              suffix="€"
              title={t("pendingThisMonth")}
              icon="fas fa-file-invoice"
              isDark
            />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <StatsCard.Number
            color="warning"
            number={totalMonthPaidOut}
            suffix="€"
            title={t("paidOutThisMonth")}
            icon="fas fa-file-invoice"
            isDark
          />
        </Col>
      </Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col xl={12}>
            <MonthlyEarningsGraphCard
              selectedYear={selectedYear}
              setYear={(y: number) => setYear(String(y))}
              yearDataMap={yearDataMap}
              isLoading={isLoadingStats}
            />
          </Col>
          <Col xl={12}>
            <YearChargesListCard
              {...usersPaginationProps}
              year={year}
              setYear={(y) => setYear(String(y))}
              perUser={pageUsers}
              total={total}
              onUserClick={({ id }: { id?: number | string }) => id && navigate(`/users/${id}`)}
              isLoading={isLoadingStats}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default MRR;
